import dayjs from "dayjs";

import { LOCAL_STORAGE_KEY, PREFECTURE_OPTIONS } from "../../constants/common";

export const getToken = () => {
  return localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN) || null;
};

export const formatCurrency = (amount: number) => {
  if (amount === 0 || amount === 0.0) {
    return "¥0";
  }
  if (amount == null || isNaN(amount)) {
    return "-";
  }
  return `¥${amount.toLocaleString("ja-JP")}`;
};

export const getPrefectureKana = (prefValue: string): string => {
  const pref = PREFECTURE_OPTIONS.find((p) => p.value === prefValue);
  return pref?.kana || "";
};

export const calcAge = (dob?: string) => {
  if (!dob) return "-";
  const d = dayjs(dob);
  if (!d.isValid()) return "-";
  const today = dayjs();
  let age = today.year() - d.year();
  if (today.month() < d.month() || (today.month() === d.month() && today.date() < d.date())) {
    age--;
  }
  return age < 0 ? "-" : String(age);
};

export const shortenFileName = (name: string, maxLength = 25) => {
  if (!name) return "";

  const dotIndex = name.lastIndexOf(".");
  if (dotIndex === -1) {
    return name.length > maxLength ? name.slice(0, maxLength - 4) + "..." : name;
  }

  const ext = name.slice(dotIndex);
  const base = name.slice(0, dotIndex);

  const allowedLength = maxLength - ext.length - 4;
  return base.length > allowedLength ? base.slice(0, allowedLength) + "..." + ext : name;
};

export const convertCurrency = (text: any) => {
  if (text) {
    return text.toLocaleString("en-US");
  } else {
    return "0";
  }
};

/**
 * Removes leading zeros from numeric strings
 * Used for sanitizing numeric input before sending to API
 * @param value - The numeric string value (e.g., "09", "000123")
 * @returns The sanitized string without leading zeros (e.g., "9", "123")
 * @example
 * removeLeadingZeros("09") // returns "9"
 * removeLeadingZeros("000123") // returns "123"
 * removeLeadingZeros("0") // returns "0"
 * removeLeadingZeros("") // returns ""
 * removeLeadingZeros("abc") // returns "abc" (non-numeric values preserved for validation)
 */
export const removeLeadingZeros = (value: string | undefined): string => {
  if (!value) return "";
  const trimmedValue = value.trim();
  if (trimmedValue === "") return "";
  // Convert to number to remove leading zeros, then back to string
  const numValue = Number(trimmedValue);
  // If not a valid number, return trimmed value (validation should catch this)
  if (isNaN(numValue)) return trimmedValue;
  // Return as string, preserving "0" if the value is actually zero
  return numValue.toString();
};

/**
 * Recursively trims all string values in an object or array
 * Used for sanitizing request data before sending to API
 * @param data - The data to trim (object, array, or primitive)
 * @returns The trimmed data
 * @example
 * trimData({ name: "  john  ", rent: "  0123  " }) // returns { name: "john", rent: "0123" }
 * trimData(["  hello  ", "  world  "]) // returns ["hello", "world"]
 * trimData("  test  ") // returns "test"
 * trimData("  0123  ") // returns "0123"
 */
export const trimData = (data: any): any => {
  // Handle null or undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle FormData - don't trim FormData
  if (data instanceof FormData) {
    return data;
  }

  // Handle string - trim it
  if (typeof data === "string") {
    return data.trim();
  }

  // Handle array - recursively trim each element
  if (Array.isArray(data)) {
    return data.map((item) => trimData(item));
  }

  // Handle object - recursively trim each property
  if (typeof data === "object") {
    const trimmed: any = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        trimmed[key] = trimData(data[key]);
      }
    }
    return trimmed;
  }

  // Handle other primitives (number, boolean, etc.) - return as is
  return data;
};

/**
 * Decodes HTML entities and converts <br> tags to newlines
 * Used for converting HTML-formatted text back to plain text
 * @param str - The HTML string to decode
 * @returns The decoded string with newlines
 */
export const decodeEntities = (str: string = "") => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str.replace(/<\/?br\s*\/?>/gi, "\n");
  return textarea.value;
};

/**
 * Encodes newlines and spaces to HTML entities
 * Used for converting plain text to HTML-formatted text
 * @param str - The plain text string to encode
 * @returns The encoded string with <br> tags and &nbsp; entities
 */
export const encodeEntities = (str: string = "") => {
  return str.replace(/\r?\n/g, "<br>").replace(/ /g, "&nbsp;");
};

export const isJapanese = (text: string) => {
  const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/;
  return japaneseRegex.test(text);
};

export const detectType = (char: string) => {
  if (/[a-zA-Z]/.test(char)) return "romaji";
  if (/[\u3040-\u309F]/.test(char)) return "hiragana";
  if (/[ｦ-ﾟ]/.test(char)) return "half-kana";
  if (/[ァ-ヶー]/.test(char)) return "full-kana";
  if (/[\u4E00-\u9FFF]/.test(char)) return "kanji";
  return "other";
};

export const splitByCharType = (input: string) => {
  const result: string[] = [];
  let buffer = "";
  let lastType = null;

  for (const ch of input) {
    const type = detectType(ch);
    if (type !== lastType && buffer) {
      result.push(buffer);
      buffer = "";
    }
    buffer += ch;
    lastType = type;
  }

  if (buffer) result.push(buffer);
  return result;
};
